import { useEffect, useState } from 'react';
import { Star } from 'lucide-react';
import { kpiApi, apiErrorText, type KpiSatisfactionLevel } from '@/shared/api';
import { useAuthStore } from '@/features/auth';
import { Button, Input, toast } from '@/shared/ui';
import styles from './SatisfactionLevelsCard.module.css';

export const SatisfactionLevelsCard: React.FC = () => {
  const canEdit = useAuthStore((s) => s.hasPermission('manage_kpi'));
  const [levels, setLevels] = useState<KpiSatisfactionLevel[]>([]);
  const [draft, setDraft] = useState<Record<number, { multiplier: string; label: string }>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    kpiApi
      .satisfactionLevels()
      .then((res) => {
        const list = res.data ?? [];
        setLevels(list);
        setDraft(
          Object.fromEntries(
            list.map((l) => [l.rating, { multiplier: String(l.multiplier), label: l.label }]),
          ),
        );
      })
      .catch((err) => {
        toast.error(apiErrorText(err, 'Не удалось загрузить множители'));
        setLevels([]);
      });
  }, []);

  const save = async () => {
    const payload = levels.map((l) => ({
      rating: l.rating,
      multiplier: Number((draft[l.rating]?.multiplier ?? String(l.multiplier)).replace(',', '.')),
      label: (draft[l.rating]?.label ?? l.label).trim(),
    }));
    const bad = payload.find(
      (p) => !Number.isFinite(p.multiplier) || p.multiplier < 0.1 || p.multiplier > 100 || !p.label,
    );
    if (bad) {
      toast.error(`Проверьте строку «${bad.rating}»: множитель 0.1–100, название не пустое`);
      return;
    }
    setSaving(true);
    try {
      const res = await kpiApi.updateSatisfactionLevels(payload);
      const saved = res.data ?? payload;
      setLevels(saved);
      setDraft(
        Object.fromEntries(
          saved.map((l) => [l.rating, { multiplier: String(l.multiplier), label: l.label }]),
        ),
      );
      toast.success('Множители сохранены');
    } catch (err) {
      toast.error(apiErrorText(err, 'Не удалось сохранить множители'));
    } finally {
      setSaving(false);
    }
  };

  if (levels.length === 0) return null;

  return (
    <div className={styles.card}>
      <h3 className={styles.heading}>
        <Star size={16} aria-hidden /> Множители за оценку заявителя
      </h3>
      <p className={styles.note}>
        Оценку ставит заявитель при подтверждении выполнения. Множитель применяется
        корректирующей строкой к уже начисленным очкам за заявку.
      </p>
      <div className={styles.rows}>
        {levels.map((l) => (
          <div key={l.rating} className={styles.row}>
            <span className={styles.rating} aria-label={`Оценка ${l.rating}`}>
              {'★'.repeat(l.rating)}
              {'☆'.repeat(5 - l.rating)}
            </span>
            <Input
              aria-label={`Название для оценки ${l.rating}`}
              value={draft[l.rating]?.label ?? l.label}
              disabled={!canEdit || saving}
              onChange={(e) =>
                setDraft((prev) => ({
                  ...prev,
                  [l.rating]: { ...prev[l.rating], label: e.target.value },
                }))
              }
            />
            <Input
              aria-label={`Множитель для оценки ${l.rating}`}
              className={styles.multiplierInput}
              inputMode="decimal"
              value={draft[l.rating]?.multiplier ?? String(l.multiplier)}
              disabled={!canEdit || saving}
              onChange={(e) =>
                setDraft((prev) => ({
                  ...prev,
                  [l.rating]: { ...prev[l.rating], multiplier: e.target.value },
                }))
              }
            />
          </div>
        ))}
      </div>
      {canEdit && (
        <div className={styles.actions}>
          <Button variant="primary" size="sm" onClick={save} disabled={saving}>
            Сохранить
          </Button>
        </div>
      )}
    </div>
  );
};
